import { useEffect, useRef, useState, type CSSProperties } from 'react';

import { cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import { AnimatedJiraTour } from '@/components/product-demos/animated-jira-tour/src/animated-jira-tour';

import type { UniformImageV2Props } from '../../image-v2/src/image-v2';

export type JiraAjAnimatedHeroProps = Pick<
	UniformImageV2Props,
	| 'width'
	| 'height'
	| 'maxWidth'
	| 'maxHeight'
	| 'tabletWidth'
	| 'desktopWidth'
	| 'tabletHeight'
	| 'desktopHeight'
	| 'aspectRatio'
	| 'objectFit'
	| 'maxPossibleWidth'
> & { showDecorativeShapes?: boolean };

const SCENE_WIDTH = 800;
const SCENE_HEIGHT = 550;

// ADS sizing types do not support the CSS variables required for Uniform's authored dimensions.
const styles = cssMap({
	container: {
		position: 'relative',
		width: 'var(--jira-hero-width)',
		height: 'var(--jira-hero-height)',
		maxWidth: 'var(--jira-hero-max-width)',
		maxHeight: 'var(--jira-hero-max-height)',
		aspectRatio: 'var(--jira-hero-aspect-ratio)',
		'@media (min-width: 48rem)': {
			width: 'var(--jira-hero-tablet-width, var(--jira-hero-width))',
			height: 'var(--jira-hero-tablet-height, var(--jira-hero-height))',
		},
		'@media (min-width: 64rem)': {
			width:
				'var(--jira-hero-desktop-width, var(--jira-hero-tablet-width, var(--jira-hero-width)))',
			height:
				'var(--jira-hero-desktop-height, var(--jira-hero-tablet-height, var(--jira-hero-height)))',
		},
	},
	scene: {
		position: 'absolute',
		width: '800px',
		height: '550px',
		transformOrigin: 'top left',
		pointerEvents: 'none',
	},
	tour: {
		position: 'absolute',
		insetBlockStart: 0,
		insetInlineStart: 0,
		width: '100%',
		height: '100%',
		transform: 'scale(0.8)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1,
	},
	shapes: { position: 'absolute', width: '100%', height: '95%' },
	yellowShape: { position: 'absolute', insetBlockStart: 0, insetInlineEnd: 0 },
	greenShape: { position: 'absolute', insetBlockEnd: 0, insetInlineStart: 0 },
});

export const JiraAjAnimatedHero = ({
	width = '100%',
	height = 'auto',
	maxWidth = '100%',
	maxHeight,
	tabletWidth,
	desktopWidth,
	tabletHeight,
	desktopHeight,
	aspectRatio = '800/550',
	showDecorativeShapes = true,
}: JiraAjAnimatedHeroProps) => {
	const intl = useIntl();
	const containerRef = useRef<HTMLDivElement>(null);
	const [size, setSize] = useState({ width: 0, height: 0 });
	// The server and first client render agree, and motion only starts after checking the preference.
	const [reducedMotion, setReducedMotion] = useState(true);

	useEffect(() => {
		const query = window.matchMedia('(prefers-reduced-motion: reduce)');
		const update = () => setReducedMotion(query.matches);
		update();
		query.addEventListener('change', update);
		return () => query.removeEventListener('change', update);
	}, []);

	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;
		const observer = new ResizeObserver((entries) => {
			const entry = entries[0];
			if (!entry) return;
			const { width: measuredWidth, height: measuredHeight } = entry.contentRect;
			setSize((previous) =>
				previous.width === measuredWidth && previous.height === measuredHeight
					? previous
					: { width: measuredWidth, height: measuredHeight },
			);
		});
		observer.observe(container);
		return () => observer.disconnect();
	}, []);

	const scale = Math.max(0, Math.min(size.width / SCENE_WIDTH, size.height / SCENE_HEIGHT));
	const sizing: CSSProperties & { [key: `--jira-hero-${string}`]: string | undefined } = {
		'--jira-hero-width': width,
		'--jira-hero-height': height,
		'--jira-hero-max-width': maxWidth,
		'--jira-hero-max-height': maxHeight,
		'--jira-hero-tablet-width': tabletWidth,
		'--jira-hero-desktop-width': desktopWidth,
		'--jira-hero-tablet-height': tabletHeight,
		'--jira-hero-desktop-height': desktopHeight,
		'--jira-hero-aspect-ratio': aspectRatio,
	};

	return (
		<div
			ref={containerRef}
			css={styles.container}
			style={sizing}
			role="img"
			aria-label={intl.formatMessage({
				id: 'jira-aj-animated-hero.description',
				defaultMessage:
					'Jira board showing an issue progressing from To Do through In Progress to Done.',
			})}
		>
			<div
				data-testid="jira-aj-scene"
				css={styles.scene}
				aria-hidden="true"
				ref={(node) => {
					node?.setAttribute('inert', '');
				}}
				style={{
					transform: `scale(${scale})`,
					left: (size.width - SCENE_WIDTH * scale) / 2,
					top: (size.height - SCENE_HEIGHT * scale) / 2,
				}}
			>
				{showDecorativeShapes && (
					<div data-testid="jira-aj-decorative-shapes" css={styles.shapes}>
						<svg
							css={styles.yellowShape}
							width="311"
							height="220"
							viewBox="0 0 311 220"
							fill="none"
							focusable="false"
						>
							<path
								d="M7.34559 1.71298L310.956 0.712733L305.579 218.935L0.637246 219.618L7.34559 1.71298Z"
								fill="#FFA900"
							/>
						</svg>
						<svg
							css={styles.greenShape}
							width="252"
							height="219"
							viewBox="0 0 252 219"
							fill="none"
							focusable="false"
						>
							<path
								d="M2.76007 0.226074H251.614V212.007L0.0842285 218.146L2.76007 0.226074Z"
								fill="#82B536"
							/>
						</svg>
					</div>
				)}
				<div css={styles.tour}>
					<AnimatedJiraTour
						layoutMode="fixed-scene"
						displayMode={reducedMotion ? 'static' : 'animated'}
					/>
				</div>
			</div>
		</div>
	);
};
